/*
 * Shopify Aurora custom wishlist
 * Add this file to Assets as wishlist.js, then load it once with `defer` in theme.liquid.
 */
(() => {
  'use strict';

  // Prevent duplicate listeners if the theme re-runs scripts during a page transition.
  if (window.__smulyWishlistLoaded) return;
  window.__smulyWishlistLoaded = true;

  const STORAGE_KEY = 'smuly.wishlist.v2';
  const CHANGE_EVENT = 'wishlist:change';
  const WISHLIST_PATHS = ['/pages/wishlist', '/pages/wishlist-page'];

  const asString = (value) => (value == null ? '' : String(value));
  const escapeHtml = (value) => {
    const element = document.createElement('span');
    element.textContent = value == null ? '' : String(value);
    return element.innerHTML;
  };

  class Wishlist {
    constructor() {
      this.items = this.read();
      this.renderTimer = null;

      document.addEventListener('click', (event) => this.handleClick(event));
      document.addEventListener('change', (event) => {
        if (event.target.matches('form [name="id"]')) this.scheduleRender();
      });
      document.addEventListener('variant:update', () => this.scheduleRender());
      document.addEventListener('shopify:section:load', () => this.scheduleRender());
      document.addEventListener('shopify:section:reorder', () => this.scheduleRender());
      document.addEventListener(CHANGE_EVENT, () => this.render());

      window.addEventListener('storage', (event) => {
        if (event.key !== STORAGE_KEY) return;
        this.items = this.read();
        this.emitChange();
      });

      this.render();
    }

    normalizeItem(item) {
      if (!item || !item.productId || !item.variantId || !item.handle) return null;

      const handle = asString(item.handle).replace(/^\/products\//, '').replace(/^\/+|\/+$/g, '');
      if (!handle) return null;

      const variantId = asString(item.variantId);
      return {
        productId: asString(item.productId),
        variantId,
        handle,
        title: asString(item.title) || handle,
        variantTitle: asString(item.variantTitle),
        url: `/products/${encodeURIComponent(handle)}?variant=${encodeURIComponent(variantId)}`,
        image: asString(item.image),
        price: Number(item.price) || 0,
        compareAtPrice: Number(item.compareAtPrice) || 0,
        available: item.available !== false
      };
    }

    read() {
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        if (!Array.isArray(saved)) return [];

        // One entry per product: retain the most recently saved variant.
        return [...new Map(saved
          .map((item) => this.normalizeItem(item))
          .filter(Boolean)
          .map((item) => [item.productId, item]))
          .values()];
      } catch (_) {
        return [];
      }
    }

    save(items) {
      this.items = items.map((item) => this.normalizeItem(item)).filter(Boolean);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (_) {
        // Private browsing or a full localStorage should not break the storefront UI.
      }
      this.emitChange();
    }

    emitChange() {
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: this.items } }));
    }

    hasVariant(variantId) {
      return this.items.some((item) => asString(item.variantId) === asString(variantId));
    }

    toggle(item) {
      const normalized = this.normalizeItem(item);
      if (!normalized) return;

      const current = this.items.find((saved) => saved.productId === normalized.productId);
      if (current && current.variantId === normalized.variantId) {
        this.save(this.items.filter((saved) => saved.productId !== normalized.productId));
        return;
      }

      this.save([
        ...this.items.filter((saved) => saved.productId !== normalized.productId),
        normalized
      ]);
    }

    remove(variantId) {
      this.save(this.items.filter((item) => item.variantId !== asString(variantId)));
    }

    selectedVariantId(button, product) {
      const details = button.closest('.product-details');
      const productId = button.dataset.productId;
      const selector = productId
        ? `product-form-component[data-product-id="${CSS.escape(productId)}"] [name="id"]`
        : '';
      const formInput = selector ? document.querySelector(selector) : null;
      return formInput?.value
        || details?.querySelector('form [name="id"]')?.value
        || button.dataset.wishlistVariantId
        || product.selected_or_first_available_variant?.id
        || product.variants?.[0]?.id;
    }

    buildItem(button, product) {
      if (!product || !Array.isArray(product.variants)) return null;
      const selectedId = this.selectedVariantId(button, product);
      const variant = product.variants.find((candidate) => asString(candidate.id) === asString(selectedId)) || product.variants[0];
      if (!variant || !product.id || !product.handle) return null;

      const featuredImage = variant.featured_image?.src
        || product.featured_image?.src
        || product.featured_image
        || product.images?.[0]?.src
        || product.images?.[0]
        || '';

      return this.normalizeItem({
        productId: product.id,
        variantId: variant.id,
        handle: product.handle,
        title: product.title,
        variantTitle: variant.title === 'Default Title' ? '' : variant.title,
        image: featuredImage,
        price: variant.price,
        compareAtPrice: variant.compare_at_price,
        available: variant.available !== false
      });
    }

    embeddedProduct(button) {
      const json = button.closest('.product-details')?.querySelector('[data-wishlist-product]')?.textContent;
      if (!json) return null;
      try {
        return this.buildItem(button, JSON.parse(json));
      } catch (_) {
        return null;
      }
    }

    async resolveProduct(button) {
      const embedded = this.embeddedProduct(button);
      if (embedded) return embedded;

      const handle = button.dataset.productHandle;
      if (!handle) return null;
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' }
        });
        return response.ok ? this.buildItem(button, await response.json()) : null;
      } catch (_) {
        return null;
      }
    }

    async handleClick(event) {
      const toggle = event.target.closest('[data-wishlist-toggle]');
      if (toggle) {
        event.preventDefault();
        const item = await this.resolveProduct(toggle);
        if (item) this.toggle(item);
        return;
      }

      const remove = event.target.closest('[data-wishlist-remove]');
      if (remove) {
        event.preventDefault();
        this.remove(remove.dataset.wishlistRemove);
        return;
      }

      const addToCart = event.target.closest('[data-wishlist-add-to-cart]');
      if (addToCart) {
        event.preventDefault();
        this.addToCart(addToCart);
      }
    }

    render() {
      this.renderCount();
      this.syncToggleButtons();
      document.querySelectorAll('wishlist-dashboard').forEach((dashboard) => dashboard.render());
    }

    scheduleRender() {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = window.setTimeout(() => this.render(), 0);
    }

    renderCount() {
      const count = this.items.length;
      const label = `Wishlist, ${count} ${count === 1 ? 'item' : 'items'}`;
      document.querySelectorAll('[data-wishlist-count]').forEach((node) => {
        node.textContent = count;
        node.classList.toggle('wishlist-bubble--visible', count > 0);
      });
      document.querySelectorAll('[data-wishlist-count-label]').forEach((node) => { node.textContent = label; });
      document.querySelectorAll('[data-wishlist-link]').forEach((node) => node.setAttribute('aria-label', label));
    }

    syncToggleButtons() {
      document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
        const item = this.embeddedProduct(button);
        const active = Boolean(item && this.hasVariant(item.variantId));
        button.dataset.active = String(active);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'} ${item?.title || 'product'} ${active ? 'from' : 'to'} wishlist`);
        const text = button.querySelector('[data-wishlist-button-text]');
        if (text) text.textContent = active ? 'Remove from wishlist' : 'Add to wishlist';
      });
    }

    async addToCart(button) {
      const variantId = button.dataset.wishlistAddToCart;
      if (!variantId || button.disabled) return;

      const originalText = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding…';
      try {
        const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map((node) => node.dataset.sectionId)
          .filter(Boolean))];
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: sectionIds,
            sections_url: window.location.pathname
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Unable to add this item to cart.');

        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: result, data: { itemCount: cart.item_count, source: 'wishlist', variantId, sections: result.sections || {} } }
        }));
        document.querySelector('cart-drawer-component')?.open?.();
        button.textContent = 'Added to cart';
      } catch (error) {
        button.textContent = error.message || 'Could not add to cart';
      } finally {
        window.setTimeout(() => {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = originalText;
        }, 1600);
      }
    }
  }

  class WishlistDashboard extends HTMLElement {
    connectedCallback() {
      this.render();
    }

    render() {
      const wishlist = window.ShopifyWishlistEngine;
      if (!wishlist) return;
      this.setAttribute('aria-busy', 'false');
      this.innerHTML = wishlist.items.length
        ? `<div class="wishlist-grid-container">${wishlist.items.map((item) => this.card(item)).join('')}</div>`
        : this.empty();
    }

    money(cents) {
      const currency = window.Shopify?.currency?.active || window.Shopify?.currency?.shop_currency || 'INR';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency', currency
      }).format((Number(cents) || 0) / 100);
    }

    card(item) {
      const image = item.image
        ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy" width="600" height="600">`
        : '';
      const compare = item.compareAtPrice > item.price
        ? `<s class="wishlist-price-compare">${this.money(item.compareAtPrice)}</s>`
        : '';
      const unavailable = item.available === false;
      const url = escapeHtml(item.url);
      return `<article class="wishlist-product-card">
        <a class="wishlist-image-wrap" href="${url}">${image}</a>
        <div class="wishlist-card-details">
          <h2 class="wishlist-card-title"><a href="${url}">${escapeHtml(item.title)}</a></h2>
          ${item.variantTitle ? `<p class="wishlist-card-variant">${escapeHtml(item.variantTitle)}</p>` : ''}
          <div class="wishlist-card-price-wrapper"><span class="wishlist-price-regular">${this.money(item.price)}</span>${compare}</div>
          <div class="wishlist-action-btns">
            <a class="wishlist-btn-view-product" href="${url}">View product</a>
            <button class="wishlist-btn-add-cart" type="button" data-wishlist-add-to-cart="${escapeHtml(item.variantId)}" ${unavailable ? 'disabled aria-disabled="true"' : ''}>${unavailable ? 'Sold out' : 'Add to cart'}</button>
            <button class="wishlist-btn-remove" type="button" data-wishlist-remove="${escapeHtml(item.variantId)}">Remove</button>
          </div>
        </div>
      </article>`;
    }

    empty() {
      return '<div class="wishlist-empty-state"><h2>Your Wishlist is Empty</h2><p>Save products you love and find them here anytime.</p><a href="/collections/all" class="wishlist-btn-continue">Continue Shopping</a></div>';
    }
  }

  if (!customElements.get('wishlist-dashboard')) customElements.define('wishlist-dashboard', WishlistDashboard);
  window.ShopifyWishlistEngine = new Wishlist();

  const ensureWishlistPage = () => {
    const path = window.location.pathname.replace(/\/$/, '').toLowerCase();
    if (!WISHLIST_PATHS.includes(path) || document.querySelector('wishlist-dashboard')) return;
    const main = document.querySelector('#MainContent');
    if (!main) return;

    const wrapper = document.createElement('section');
    wrapper.className = 'section page-width-content wishlist-section wishlist-section--fallback';
    wrapper.setAttribute('data-wishlist-page', '');
    wrapper.innerHTML = '<div class="wishlist-page__inner"><wishlist-dashboard aria-live="polite" aria-busy="true"></wishlist-dashboard></div>';
    main.append(wrapper);
  };

  const refreshAfterNavigation = () => {
    ensureWishlistPage();
    window.ShopifyWishlistEngine?.scheduleRender();
  };

  // Aurora can replace #MainContent without a full reload. Observe it and also
  // listen to common theme/navigation lifecycle events.
  const observer = new MutationObserver((records) => {
    const needsRefresh = records.some((record) => {
      if (record.target instanceof Element && record.target.id === 'MainContent') return true;
      return [...record.addedNodes].some((node) => {
        if (!(node instanceof Element)) return false;
        // Aurora can replace the header separately from the page. Repaint its
        // new badge from localStorage so the count never disappears or flashes.
        return node.id === 'MainContent'
          || node.matches('[data-wishlist-count], [data-wishlist-count-label], [data-wishlist-link]')
          || Boolean(node.querySelector('[data-wishlist-count], [data-wishlist-count-label], [data-wishlist-link]'));
      });
    });
    if (needsRefresh) refreshAfterNavigation();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  ['DOMContentLoaded', 'pageshow', 'shopify:section:load', 'page:loaded', 'turbo:load'].forEach((name) => {
    document.addEventListener(name, refreshAfterNavigation);
  });
  refreshAfterNavigation();
})();
